<template>
  <transition name="fade">
    <div v-if="isVisible" class="backdrop" @click="toggleSidebar"></div>
  </transition>
  <transition name="slide">
    <div v-if="isVisible" class="shopping-cart-sidebar">
      <div class="sidebar-content d-flex flex-column">
        <div class="d-flex justify-content-between align-items-center p-3 border-bottom">
          <div class="d-flex justify-content-between align-items-center">
            <h4 class="me-3">{{ title }}</h4>
            <a class="link-offset-2 cursor-pointer" @click="clearCart">
              {{ labelEmptyCart }}
            </a>
          </div>
          <button
            type="button"
            class="btn-close"
            aria-label="Close"
            @click="toggleSidebar"
          ></button>
        </div>
        <ProgressBar
          v-if="progressBarRequired && !getBalanceLogicState"
          minimunLogic
          :containerBaseClasses="'alert p-3 mx-2 mt-2'"
          :progressClass="'progress mb-3 mt-2'"
          :logicClass="'alert-warning'"
          :logicAcomplishedClass="'alert-success'"
          :logicData="minimumPurchase"
          :total="shoppingStore.cartTotalPrice"
          :messageLogicAcomplished="messageLogicAcomplished"
          :formatCurrency="formatCurrency"
        />
        <ProgressBar
          v-if="
            requireProgressBarCredit &&
            getBalanceLogicState &&
            (agreementData?.agreementType === 1 || !agreementData?.agreementType)
          "
          remainLogic
          :containerBaseClasses="'alert p-3 mx-2 mt-2 fs-5'"
          :progressClass="'progress mb-3 mt-2'"
          :logicClass="'alert-light'"
          :logicAcomplishedClass="'alert-warning'"
          :logicData="remainingBalance"
          :total="totalToPay"
          :userBalance="userBalance"
          :messageLogicAcomplished="messageLogicAcomplishedCredit"
          :messageLogicNotAcomplished="messageLogicNotAcomplishedCredit"
          :formatCurrency="formatCurrency"
        />
        <ProgressBar
          v-if="
            requireProgressBarCredit && getBalanceLogicState && agreementData?.agreementType === 2
          "
          minimunLogic
          :containerBaseClasses="'alert p-3 mx-2 mt-2 fs-5'"
          :progressClass="'progress mb-3 mt-2'"
          :logicClass="'alert-light'"
          :logicAcomplishedClass="'alert-success'"
          :logicData="agreementData.agreement_minimum_purchase"
          :total="shoppingStore.cartTotalPrice"
          :messageLogicAcomplished="messageLogicAcomplishedCredit2"
          :messageLogicNotAcomplished="messageLogicNotAcomplishedCredit2"
          :formatCurrency="formatCurrency"
        />
        <div class="flex-grow-1 p-3 overflow-auto">
          <ProductCartList
            hideOriginalFooter
            @showProductDetail="showProductDetail"
            @addOne="addOne"
            @changeAmount="changeAmount"
            @subtractOne="subtractOne"
            @removeProduct="removeProduct"
          />
        </div>

        <CartSideMenuFooter
          :configFooterContent="footerContent"
          :classFooter="'p-3 border-top'"
          @goToCart="goToShoppingCartView"
        />
      </div>
    </div>
  </transition>
</template>

<script setup>
import ProductCartList from '@/components/products/ProductCartList.vue'
import Swal from 'sweetalert2'
import { computed, getCurrentInstance } from 'vue'
import { useShoppingStore } from '@/stores/shopping'
import { useSiteConfigStore } from '@/stores/config/site'
import { useCartConfigStore } from '@/stores/config/cart'
import { useUserStore } from '@/stores/user'
import ProgressBar from '@/components/shared/ProgressBar.vue'
import CartSideMenuFooter from '@/components/layout/cart/CartSideMenuFooter.vue'
import router from '@/router'
import { useTierMessageNotifier } from '@/composables/useTierMessageNotifier'
import { usePriceKey } from '@/composables/usePriceKey'

useTierMessageNotifier()
const siteConfigStore = useSiteConfigStore()
const cartConfigStore = useCartConfigStore()
const { shouldApplyDynamicPrice } = usePriceKey()

const minimumPurchase = computed(() => siteConfigStore.getMinimumPurchasePrice)
const {
  footerValues,
  progressBarRequired,
  messageCreditBalanceByUserAvailable,
  messageEmptyCart,
  messageEmptyCartBtn,
  title,
  labelEmptyCart,
  logicCreditBalanceByUser: isLogicCreditBalanceActive
} = cartConfigStore?.getSideCartMenuConfig || {}
const creditLogicState = computed(() => isLogicCreditBalanceActive && getBalanceLogicState.value)
const requireProgressBarCredit = computed(() => cartConfigStore.getRequiredProgressBarCredit)

const messageLogicAcomplished = computed(() => {
  const message = cartConfigStore.getMinimumPurchaseMessageAcomplished
  if (!message) {
    console.warn('El mensaje de lógica cumplida no está definido.')
    return ''
  }
  return message
})

const messageLogicAcomplishedCredit = computed(() => {
  return cartConfigStore.getMessageCreditBalance
    ? cartConfigStore.getMessageCreditBalance
    : 'Ha excedido su saldo'
})

const messageLogicNotAcomplishedCredit = computed(
  () =>
    `<span>${messageCreditBalanceByUserAvailable} <strong>${formatCurrency(
      remainingBalance.value
    )}</strong></span>`
)

const messageLogicAcomplishedCredit2 = computed(() => {
  return `<span>¡Felicidades! Alcanzaste la compra mínima de <strong>${formatCurrency(agreementData.value.agreement_minimum_purchase)}</strong>. Tu bono será aplicado en esta compra.</span>`
})
const messageLogicNotAcomplishedCredit2 = computed(
  () =>
    `<span>Bono disponible al alcanzar tu compra mínima. Agrega <strong>${formatCurrency(agreementData.value.agreement_minimum_purchase - shoppingStore.cartTotalPrice)}</strong> más al carrito.</span>`
)
const footerContent = computed(() => {
  const content = []
  let configData = footerValues // arreglo en el orden de la marca

  if (
    creditLogicState.value &&
    (!agreementData.value?.agreementType || agreementData.value?.agreementType === 1)
  ) {
    configData = ['userBalance', 'subtotal', 'discount', 'total']
  } else {
    configData = footerValues
  }

  const isDynamicPrice = shouldApplyDynamicPrice.value
  const oldTotal =
    creditLogicState.value &&
    (!agreementData.value?.agreementType || agreementData.value?.agreementType === 1)
      ? shoppingStore.cartTotalPrice - userBalance.value
      : shoppingStore.cartTotalPrice
  const newTotal =
    creditLogicState.value &&
    (!agreementData.value?.agreementType || agreementData.value?.agreementType === 1)
      ? shoppingStore.cartTotalPriceWithPricingRules - userBalance.value
      : shoppingStore.cartTotalPriceWithPricingRules
  const oldDiscount =
    creditLogicState.value &&
    (!agreementData.value?.agreementType || agreementData.value?.agreementType === 1)
      ? shoppingStore.cartTotalDiscount + (userBalance.value - remainingBalance.value)
      : shoppingStore.cartTotalDiscount
  const newDiscount =
    creditLogicState.value &&
    (!agreementData.value?.agreementType || agreementData.value?.agreementType === 1)
      ? shoppingStore.cartTotalDiscountWithPricingRules +
        (userBalance.value - remainingBalance.value)
      : shoppingStore.cartTotalDiscountWithPricingRules

  configData.forEach((key) => {
    // ... armar el objeto para cada label como lo haces actualmente
    let item = null

    if (key === 'subtotal') {
      item = {
        label: 'Subtotal',
        value: formatCurrency(shoppingStore.cartSubTotalPrice)
      }
    } else if (key === 'discount') {
      if (!isDynamicPrice && oldDiscount > 0) {
        item = {
          label: 'Descuento',
          classLabel: 'text-success',
          classValue: 'text-success',
          value: formatCurrency(oldDiscount)
        }
      }

      // Muestra el descuento dinámico si aplica
      if (isDynamicPrice && newDiscount > 0) {
        item = {
          label: 'Descuento',
          classLabel: 'text-success',
          classValue: 'text-success',
          value: formatCurrency(newDiscount)
        }
      }
    } else if (key === 'total') {
      // Si aplica la regla, agregas el nuevo total dinámico aquí mismo
      if (isDynamicPrice && oldTotal !== newTotal) {
        // Primero el total original
        item = {
          label: 'Total a pagar',
          isTotal: true,
          classLabel: isDynamicPrice
            ? 'fw-bold text-muted text-decoration-line-through'
            : 'fw-bold',
          classValue: isDynamicPrice
            ? 'fw-bold text-muted text-decoration-line-through'
            : 'fw-bold',
          value: formatCurrency(oldTotal < 0 ? 0 : oldTotal),
          showStrikethrough: isDynamicPrice
        }
        content.push(item)
        content.push({
          label: 'Total con promoción',
          isTotal: true,
          classLabel: 'fw-bold text-success',
          classValue: 'fw-bold text-success',
          value: formatCurrency(newTotal < 0 ? 0 : newTotal),
          highlight: true
        })
        return // Ya agregaste ambos, no sigas con este ciclo
      } else {
        // Total normal
        content.push({
          label: 'Total a pagar',
          isTotal: true,
          classLabel: 'fw-bold',
          classValue: 'fw-bold',
          value: formatCurrency(oldTotal < 0 ? 0 : oldTotal)
        })
        return
      }
    } else if (key === 'userBalance') {
      item = {
        label: 'Saldo de bono',
        value: formatCurrency(remainingBalance.value)
      }
    }

    if (item) content.push(item)
  })

  return content
})
const globalProperties = getCurrentInstance().appContext.config.globalProperties
const formatCurrency = globalProperties.$formatcurrency

const shoppingStore = useShoppingStore()
const userStore = useUserStore()

const agreementState = computed(() => userStore.agreementStateByRoute)

const primaryColor = computed(() => siteConfigStore.getPrimaryColor)

const isVisible = computed({
  get: () => shoppingStore.cartIsVisible,
  set: (newValue) => {
    shoppingStore.updateCartVisibility(newValue)
  }
})
const remainingBalance = computed(() => shoppingStore.getUserBalanceMinusTotal)
const userBalance = computed({
  get: () => shoppingStore.getUserBalance,
  set: (newValue) => {
    shoppingStore.updateUserBalance(newValue)
  }
})
const getBalanceLogicState = computed(() => shoppingStore.getBalanceLogicState)
const agreementData = computed(() => userStore.getAgreementData)

const totalToPay = computed(() => shoppingStore.cartTotalPrice - userBalance.value)

const addOne = (id) => {
  shoppingStore.addOne(id)
}

const changeAmount = (id, amount) => {
  shoppingStore.changeAmount(id, amount)
}

const subtractOne = (id) => {
  shoppingStore.subtractOne(id)
}

const removeProduct = (index) => {
  shoppingStore.deleteItem(index)
}

const toggleSidebar = () => {
  isVisible.value = !isVisible.value
}

const showProductDetail = (id) => {
  router.push({ name: 'productDetail', params: { id } })
  toggleSidebar()
}

const clearCart = () => {
  Swal.fire({
    title: messageEmptyCart || '¿Estás seguro de vaciar el carrito?',
    html: `
      <p>Todos los productos agregados serán eliminados y esta acción no se puede deshacer.</p>
      <p>¿Deseas continuar?</p>
    `,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: primaryColor.value,
    cancelButtonColor: '#9499a1',
    confirmButtonText: messageEmptyCartBtn || 'Vaciar carrito',
    cancelButtonText: 'Cancelar'
  }).then((result) => {
    if (result.isConfirmed) {
      shoppingStore.emptyCart()
      shoppingStore.hideCart()
    }
  })
}

const goToShoppingCartView = () => {
  if (agreementState.value) {
    router.push({ name: 'agreement.shopping_cart' })
  } else {
    router.push({ name: 'shoppingCart' })
  }
  shoppingStore.hideCart()
}
</script>
